import * as msal from '@azure/msal-browser';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as microsoftTeams from '@microsoft/teams-js';
import { $settings } from '@/provider/settings';
import localStorageHelper from '@/helper/localStorageHelper';
import { isInTeams } from '@/utils/isInTeams';
import { isInOffice } from '@/utils/isInOffice';
import { useAuthStore } from '@/store/auth';

class AuthService {
  constructor() {
    this.profile = localStorageHelper.getProfile();
    this.axiosInstance = axios.create();
    const { clientId, authority, redirectUri, postLogoutRedirectUri } =
      $settings.authentication;

    this.msalConfig = {
      auth: {
        clientId,
        authority,
        redirectUri,
        postLogoutRedirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
      },
      system: {
        asyncPopups: true,
        allowRedirectInIframe: true,
        /*
           *** DEBUGGING OPTIONS FOR MSAL ***
           *** DONT USE IN PRODUCTION ***
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (containsPii) {
                return;
              }
              switch (level) {
                case 0:
                  console.error(message);
                  return;
                case 1:
                  console.warn(message);
                  return;
                case 2:
                  console.info(message);
                  return;
                case 3:
                  console.debug(message);
                  return;
                case 4:
                  console.debug(message);
              }
            },
            logLevel: 3,
            piiLoggingEnabled: false,
          },
           */
        windowHashTimeout: 30000,
        iframeHashTimeout: 30000,
        loadFrameTimeout: 30000,
      },
    };
    // Teams Handling
    microsoftTeams.initialize();

    if (isInTeams()) {
      microsoftTeams.authentication.getAuthToken({
        resources: [`${$settings.authentication.teamsScope}`],
        silent: false,
        successCallback: async (token) => {
          await this.getAccessToken(token);
          this.selectAccount(null, token);
        },
        failureCallback: (error) => {
          console.error(error);
        },
      });
    }

    this.msalInstance = new msal.PublicClientApplication(this.msalConfig);
  }

  async login() {
    if (await this.isAuthenticated()) {
      return;
    }

    if (!isInTeams()) {
      await this.msalInstance.loginPopup().then((response) => {
        this.selectAccount(response);
      });
    }
  }

  async logout() {
    return new Promise((resolve) => {
      if (!isInTeams()) {
        this.msalInstance
          .logoutPopup({
            account: this.msalInstance.getAccountByHomeId(this.accountId),
            postLogoutRedirectUri: this.msalConfig.auth.postLogoutRedirectUri,
            mainWindowRedirectUri: this.msalConfig.auth.postLogoutRedirectUri,
          })
          .then(() => {
            console.info('Logout successful');
            resolve(true);
          })
          .catch((error) => {
            console.error('Logout failed:', error);
            resolve(false);
          });
      }
    });
  }

  async getToken() {
    let token;
    if (isInTeams()) {
      token = await this.getCachedToken();
    } else if (isInOffice()) {
      token = this.getOfficeToken([
        `${$settings.authentication.audience}/webX`,
      ]);
    } else {
      token = this.getAccessTokenByScopes([
        `${$settings.authentication.audience}/webX`,
      ]);
    }
    return token;
  }

  async getGraphToken() {
    const accessTokenRW = await this.getAccessTokenByScopes([
      'Calendars.ReadWrite',
    ]);
    const accessTokenR = await this.getAccessTokenByScopes(['Calendars.Read']);
    return accessTokenRW || accessTokenR;
  }

  async getOfficeToken(scopeParameter) {
    try {
      const myAccounts = this.msalInstance.getAllAccounts();
      if (myAccounts?.length > 0) {
        const firstAccount = myAccounts[0];
        const tokenAuthority = await this.msalInstance.acquireTokenSilent({
          scopes: scopeParameter,
          account: firstAccount,
        });
        return tokenAuthority;
      }
    } catch (error) {
      console.error(error);
    }
    return null;
  }

  selectAccount(response = null, token = null) {
    if (response !== null) {
      this.profile = {
        ...this.profile,
        username: response.account.username,
        accountId: response.account.homeAccountId,
      };
      token = response.idToken;
    }
    const currentAccounts = this.msalInstance.getAllAccounts();
    if (isInTeams() || isInOffice()) {
      const decodedToken = token && jwtDecode(token);
      const { oid, tid } = decodedToken;
      decodedToken.accountId = `${oid}.${tid}`;
      this.profile = { ...this.profile, ...decodedToken };
    } else {
      if (currentAccounts.length === 0) {
        console.warn('No accounts detected.');
      }
      if (currentAccounts.length > 1) {
        console.warn('Multiple accounts detected.');
      } else if (currentAccounts.length === 1) {
        this.profile = {
          ...this.profile,
          accountId: currentAccounts[0].homeAccountId,
          username: currentAccounts[0].username,
          fullName: currentAccounts[0].name,
        };
      }
    }
  }

  static cacheToken(tokenResponse) {
    const expiry = new Date();
    expiry.setSeconds(expiry.getSeconds() + tokenResponse.expiresIn - 300);
    localStorage.setItem('teams-token', tokenResponse.accessToken);
    localStorage.setItem('teams-token-expiry', expiry.toISOString());
    localStorage.setItem('teams-refresh-token', tokenResponse.refreshToken);
  }

  async getCachedToken() {
    let accessToken = null;
    const currentDate = new Date();
    const expiryString = localStorage.getItem('teams-token-expiry');
    if (!expiryString) {
      accessToken = await this.getAccessToken();
    }
    const expiryDate = new Date(expiryString);
    if (currentDate.getTime() > expiryDate.getTime()) {
      try {
        accessToken = await this.refreshTeamsToken();
      } catch {
        accessToken = await this.getAccessToken();
      }
    } else {
      const expiresIn = localStorage.getItem('teams-token-expiry');
      accessToken = {
        accessToken: localStorage.getItem('teams-token'),
        extExpiresOn: (expiresIn && new Date(expiresIn)) || new Date(),
      };
    }
    return accessToken;
  }

  async getAccessTokenByScopes(scopes) {
    return this.msalInstance
      .acquireTokenSilent({
        scopes,
        account: this.msalInstance.getAccountByHomeId(this.profile.accountId),
      })
      .then((response) => response)
      .catch((silentError) => {
        // call acquireTokenPopup in case of acquireTokenSilent failure
        // due to consent or interaction required
        if (
          silentError.errorCode === 'consent_required' ||
          silentError.errorCode === 'interaction_required' ||
          silentError.errorCode === 'login_required'
        ) {
          this.msalInstance
            .acquireTokenPopup(scopes)
            .then((response) => response)
            .catch((popupError) => {
              console.error(popupError);
            });
        }
      });
  }

  async refreshTeamsToken() {
    const refreshResponse = await this.axiosInstance.get(
      `${$settings.middlewareURI}/token/refresh`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem('teams-refresh-token')}`,
        },
      },
    );
    AuthService.cacheToken(refreshResponse.data);
    return refreshResponse.data;
  }

  async getAccessToken(token = null) {
    const baseURL = `${$settings.middlewareURI}/token/exchange?scopes=${encodeURI(`${$settings.authentication.audience}/webX`)}`;
    const config = token
      ? { headers: { authorization: `Bearer ${token}` } }
      : {};

    const accessTokenResponse = await this.axiosInstance.get(baseURL, config);
    AuthService.cacheToken(accessTokenResponse.data);
    return accessTokenResponse.data;
  }

  getProfile() {
    return this.profile;
  }

  async isAuthenticated() {
    const token = await this.getToken();

    if (!token) {
      return false;
    }

    const { profile } = this;
    if (!profile) {
      return false;
    }

    useAuthStore().setToken(token.accessToken);
    useAuthStore().setProfile(profile);

    return Date.now() <= token.extExpiresOn.getTime();
  }
}

const authService = new AuthService();
export { authService };
